import PrintMethodToggle from '@/components/print/PrintMethodToggle';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileSpreadsheet, Play, Eye, Users, Clock, CheckCircle, AlertTriangle, Package, ArrowRightLeft, RefreshCw, Download, Filter, ChevronDown, ChevronUp, Trash2, Printer, Usb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as authApi from '@/services/authApi';
import * as printerApi from '@/services/printerApi';
import { generateTsplFromLabelData, PRINTER_CONFIGS } from '@/utils/tsplGenerator';
import { PricingTypePrompt } from '@/components/PricingTypePrompt';
import { usePricingChannels } from '@/hooks/usePricingChannels';

const FGDesignTransferSlips = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState(null);
  
  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  
  // Assign dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignSlipNumber, setAssignSlipNumber] = useState('');
  const [pickers, setPickers] = useState([]);
  const [selectedPicker, setSelectedPicker] = useState('');
  
  // USB Print state
  const [usbDevice, setUsbDevice] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [printingSlip, setPrintingSlip] = useState(null);
  const [printerModel] = useState('TSC_TE210');
  const [pricingType, setPricingType] = useState('TN');
  const availablePricingTypes = usePricingChannels();
  const [ratePrompt, setRatePrompt] = useState(null); // target SKU has no rate in the chosen pricing type → ask which one
  const [copiesPerLabel] = useState(1);
  const [arialLineSpacingMultiplier] = useState(2.5);

  // Direct to Packing state
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tagSlipNumber, setTagSlipNumber] = useState('');
  const [fgSlipSearch, setFgSlipSearch] = useState('');
  const [matchingFgSlips, setMatchingFgSlips] = useState([]);
  const [searchingFgSlips, setSearchingFgSlips] = useState(false);

  // Status colors
  const statusColors = {
    'IMPORTED': 'bg-blue-100 text-blue-800 border-blue-300',
    'ASSIGNED': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'IN_PROGRESS': 'bg-purple-100 text-purple-800 border-purple-300',
    'COMPLETED': 'bg-green-100 text-green-800 border-green-300',
    'PARTIAL': 'bg-orange-100 text-orange-800 border-orange-300'
  };
  
  useEffect(() => {
    fetchUser();
    fetchSlips();
    fetchTemplates();
  }, [statusFilter]);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/main-labels/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };
  
  const fetchUser = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };
  
  const fetchSlips = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await apiClient.get(`/wms/fg/design-transfer-slips${params}`);
      setSlips(response.data.slips || []);
    } catch (error) {
      toast.error('Failed to fetch design transfer slips');
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setImporting(true);
    setImportProgress({ phase: 'uploading' });
    
    try {
      const response = await fetch(`${apiClient.defaults.baseURL}/wms/fg/design-transfer-slips/import`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });

      // 2026-05-26 — Surface the real failure reason instead of the generic
      // "Failed to import file" toast. On production this was swallowing
      // 401/413/500/CSV-validation errors and giving operators nothing to act on.
      if (!response.ok) {
        let serverMsg = '';
        try {
          serverMsg = await response.text();
        } catch (_) { /* ignore */ }
        // Try to extract a JSON `detail` field if FastAPI threw an HTTPException
        let parsedDetail = serverMsg;
        try {
          const j = JSON.parse(serverMsg);
          parsedDetail = j.detail || j.error || j.message || serverMsg;
        } catch (_) { /* keep raw */ }
        const trimmed = String(parsedDetail || '').slice(0, 400) || `HTTP ${response.status}`;
        toast.error(`Import failed (HTTP ${response.status}): ${trimmed}`, { duration: 12000 });
        console.error('[DesignTransferImport] non-2xx response:', response.status, serverMsg);
        return;
      }

      if (!response.body) {
        toast.error('Import failed: empty response body (proxy may have stripped the stream).', { duration: 10000 });
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        // Keep the trailing partial line in the buffer for the next chunk
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              setImportProgress(data);
              
              if (data.error) {
                toast.error(`Import error: ${data.error}`, { duration: 12000 });
              } else if (data.phase === 'completed') {
                const errSnippet = (data.errors || []).length
                  ? ` (${data.errors.length} row error(s): ${data.errors.slice(0, 2).join(' | ')})`
                  : '';
                toast.success(`Imported ${data.imported} slip(s). Skipped ${data.skipped} duplicate(s).${errSnippet}`);
                fetchSlips();
              }
            } catch (e) { /* malformed line — ignore */ }
          }
        }
      }
    } catch (error) {
      const msg = error?.message || String(error);
      toast.error(`Failed to import file: ${msg}`, { duration: 12000 });
      console.error('[DesignTransferImport] threw:', error);
    } finally {
      setImporting(false);
      setImportDialogOpen(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  const handleViewDetail = async (slipNumber) => {
    try {
      const response = await apiClient.get(`/wms/fg/design-transfer-slip-detail`, { params: { slip_number: slipNumber } });
      setSelectedSlip(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch slip details');
    }
  };
  
  const handleOpenAssign = async (slipNumber) => {
    setAssignSlipNumber(slipNumber);
    setSelectedPicker('');
    
    // Fetch available FG pickers from dispatch allocation system
    try {
      const response = await apiClient.get('/wms/dispatch/available-pickers?picker_type=FG');
      const fgAvailability = response.data.availability?.FG;
      if (fgAvailability && fgAvailability.pickers?.length > 0) {
        setPickers(fgAvailability.pickers);
      } else {
        // No FG pickers set for today - show message
        toast.warning('No FG pickers available today. Please set FG pickers in Dispatch Supervisor.');
        setPickers([]);
      }
    } catch (error) {
      // Fallback - get all staff
      try {
        const response = await apiClient.get('/wms/dispatch/staff-list');
        setPickers(response.data.staff || []);
      } catch (e) {
        toast.error('Failed to fetch pickers');
        setPickers([]);
      }
    }
    
    setAssignDialogOpen(true);
  };
  
  const handleAssign = async () => {
    if (!selectedPicker) {
      toast.error('Please select a picker');
      return;
    }
    
    try {
      await apiClient.post(`/wms/fg/design-transfer-slips/${encodeURIComponent(assignSlipNumber)}/assign`, {
        staff_id: selectedPicker
      });
      toast.success('Slip assigned successfully');
      setAssignDialogOpen(false);
      fetchSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign slip');
    }
  };
  
  const handleStartPicking = (slipNumber) => {
    navigate(`/wms/fg/design-transfer-picker?slip=${encodeURIComponent(slipNumber)}`);
  };
  
  const toggleItemExpand = (idx) => {
    setExpandedItems(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };
  
  // Complete (finalize) a partially-picked slip: skips remaining items and
  // flips status to COMPLETED so stickers can be printed. Recovers slips
  // stuck at IN_PROGRESS when items were skipped before the final placement.
  const handleCompleteSlip = async (slipNumber) => {
    if (!window.confirm(
      `Mark slip "${slipNumber}" as completed?\n\nAny remaining un-picked items will be skipped, and the slip will move to COMPLETED so you can print stickers for the transferred pieces.`
    )) {
      return;
    }
    try {
      const res = await apiClient.post(`/wms/fg/design-transfer-slips/${encodeURIComponent(slipNumber)}/finalize`);
      const handover = res.data?.handover_staff;
      toast.success(
        handover
          ? `Slip completed. Handover to ${handover}.`
          : (res.data?.message || 'Slip completed.')
      );
      fetchSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete slip');
    }
  };

  // Delete design transfer slip (admin only)
  const handleDeleteSlip = async (slipNumber) => {
    if (!window.confirm(`Are you sure you want to delete design transfer slip "${slipNumber}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/admin/design-transfers/${encodeURIComponent(slipNumber)}`);
      toast.success(`Design transfer slip "${slipNumber}" deleted successfully`);
      fetchSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete design transfer slip');
    }
  };
  
  const filteredSlips = slips.filter(slip => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      slip.slip_number?.toLowerCase().includes(term) ||
      slip.assigned_to_name?.toLowerCase().includes(term)
    );
  });
  
  const canManage = user && ['Admin', 'Dispatch Supervisor', 'Dispatch Incharge'].includes(user.role);

  const handleToggleDirectToPacking = async (slipNumber) => {
    try {
      const response = await apiClient.patch(`/wms/fg/design-transfer-slips/${slipNumber}/direct-to-packing`);
      toast.success(response.data.direct_to_packing ? 'Direct to Packing enabled' : 'Direct to Packing disabled');
      fetchSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle');
    }
  };

  const handleOpenTagDialog = async (slipNumber) => {
    setTagSlipNumber(slipNumber);
    setFgSlipSearch('');
    setMatchingFgSlips([]);
    setTagDialogOpen(true);
    await searchMatchingFgSlips(slipNumber, '');
  };

  const searchMatchingFgSlips = async (slipNumber, search) => {
    setSearchingFgSlips(true);
    try {
      const response = await apiClient.get(`/wms/fg/design-transfer-slips/${slipNumber}/matching-fg-slips?search=${encodeURIComponent(search)}`);
      setMatchingFgSlips(response.data.fg_slips || []);
    } catch (error) {
      toast.error('Failed to search FG slips');
    } finally {
      setSearchingFgSlips(false);
    }
  };

  const handleTagFgSlip = async (fgSlipNumber) => {
    try {
      const response = await apiClient.post(`/wms/fg/design-transfer-slips/${tagSlipNumber}/tag-fg-slip`, {
        fg_pick_slip_number: fgSlipNumber
      });
      toast.success(`Tagged FG Pick Slip ${fgSlipNumber} (${response.data.overlap_count} barcodes matched)`);
      setTagDialogOpen(false);
      fetchSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to tag FG slip');
    }
  };


  const findTemplateByName = (templateName) => {
    if (!templateName || !templates.length) return null;
    const normalize = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').replace(/\s*\(\s*/g, '(').replace(/\s*\)\s*/g, ')').trim();
    const target = normalize(templateName);
    let match = templates.find(t => normalize(t.template_name) === target);
    if (match) return match;
    match = templates.find(t => target.includes(normalize(t.template_name)) || normalize(t.template_name).includes(target));
    return match || null;
  };

  const connectUSBPrinter = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setUsbDevice(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'}`);
      return device;
    } catch (error) {
      console.error('Error connecting USB:', error);
      toast.error('Failed to connect USB printer. Please try again.');
      return null;
    }
  };

  const handlePrintStickers = async (slipNumber, pt = pricingType) => {
    setPrintingSlip(slipNumber);
    let asking = false;
    // 2026-05-26 — Phase-tagged error reporter. The prior generic "Failed
    // to print stickers" toast hid the actual failure (label data fetch,
    // main-labels preview, USB connect, TSPL gen, or WebUSB transferOut).
    // Now each phase surfaces the real reason inline so operators can
    // self-diagnose without dev-tools.
    let phase = 'init';
    try {
      // Step 1: Get label data for this slip
      phase = 'label-data fetch';
      const labelRes = await apiClient.get(`/wms/fg/design-transfer-slips/label-data/${slipNumber}`);
      const { usns, barcodes } = labelRes.data;
      if (!usns || usns.length === 0) {
        toast.error('No transferred USNs found for this slip');
        return;
      }

      // Step 2: Fetch batch label data from main-labels API (pricing, MRP, etc.)
      phase = 'main-labels preview';
      const previewRes = await apiClient.post('/main-labels/preview-batch', {
        barcodes: barcodes,
        pricing_type: pt,
      });
      // Target SKU without a rate in this pricing type would print blank stickers (Rs.0.00) → stop and ask which pricing type to use.
      const gaps = (previewRes.data.missing_rate || []).filter((b) => barcodes.includes(b));
      if (gaps.length) {
        asking = true;
        setRatePrompt({ what: 'Slip', code: slipNumber, pt, barcodes: gaps, options: previewRes.data.rate_options || [] });
        return;
      }
      // 2026-05-26 — `/main-labels/preview-batch` returns `results` as a
      // DICT keyed by barcode (see main_labels.py L1112). The previous
      // `for (const item of results)` iteration threw
      // `TypeError: results is not iterable` on a plain object, which
      // was caught and surfaced as the generic "Failed to print stickers"
      // toast. Consume the dict directly — same pattern as
      // SFGMainLabelPrinting.jsx.
      const labelDataMap = previewRes.data.results || {};
      const missingBarcodes = barcodes.filter((bc) => !labelDataMap[bc]);
      if (missingBarcodes.length === barcodes.length) {
        toast.error(
          `main-labels/preview-batch returned no rows for any of ${barcodes.length} barcode(s). ` +
          `First barcode: ${barcodes[0]}. Check item_master for these SKUs.`
        );
        return;
      }

      // Step 3: Connect USB printer if not already connected
      phase = 'USB printer connect';
      let device = usbDevice;
      if (!device) {
        device = await connectUSBPrinter();
        if (!device) return;
      }

      // Step 4: Generate TSPL commands and send to printer
      phase = 'TSPL generation';
      const printerConfig = PRINTER_CONFIGS[printerModel];
      let allCommands = '';
      let totalLabels = 0;
      const defaultTemplate = templates[0] || null;
      let skippedNoData = 0;

      for (const usn of usns) {
        const cachedData = labelDataMap[usn.barcode];
        if (!cachedData) { skippedNoData++; continue; }
        const labelData = cachedData.label_data || {};
        const mrpFormatted = cachedData.mrp_formatted || `Rs.${labelData.mrp || 0}`;
        const templateName = usn.print_template || 'Standard';
        const template = findTemplateByName(templateName) || defaultTemplate;
        for (let copy = 0; copy < copiesPerLabel; copy++) {
          allCommands += generateTsplFromLabelData(
            labelData, template, usn.usn_code, mrpFormatted,
            usn.usn_code, printerConfig, template?.template_type,
            null, arialLineSpacingMultiplier
          );
          totalLabels++;
        }
      }

      if (allCommands && totalLabels > 0) {
        phase = 'USB transferOut';
        await printerApi.sendToUSBPrinter(device, allCommands);
        toast.success(`Printed ${totalLabels} stickers for slip ${slipNumber} (${pt} rates)`);
      } else {
        // Surface WHY no commands were generated.
        if (!templates.length) {
          toast.error('No label templates configured. Open Templates admin and add at least one template.');
        } else if (skippedNoData === usns.length) {
          toast.error(
            `All ${usns.length} USN(s) skipped: main-labels/preview-batch returned no label_data ` +
            `for any of their target barcodes. Check pricing_type="${pt}" against item_master.`
          );
        } else {
          toast.error(
            `No TSPL commands generated (${skippedNoData}/${usns.length} USNs had no label_data; ` +
            `template_name="${defaultTemplate?.template_name || 'none'}"). ` +
            `Likely a template mismatch — try editing the template or pricing_type.`
          );
        }
      }
    } catch (error) {
      console.error('Print error:', error);
      // Verbose reporter — surface the actual cause for each phase.
      const detail = (
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.response?.data?.message
      );
      const status = error?.response?.status;
      const msg = error?.message || String(error);
      if (detail) {
        toast.error(`[${phase}] ${status ? status + ' — ' : ''}${detail}`);
      } else if (status) {
        toast.error(`[${phase}] HTTP ${status} — ${msg}`);
      } else if (error?.name === 'NotFoundError' || /no device selected/i.test(msg)) {
        toast.error(`[${phase}] No USB printer selected. Click Connect Printer then retry.`);
      } else if (/transferOut|stalled|protocol error|InvalidState/i.test(msg)) {
        toast.error(
          `[${phase}] USB printer rejected the data (${msg}). ` +
          `Unplug + replug the printer or click Connect Printer to reset.`
        );
      } else {
        toast.error(`[${phase}] ${msg}`);
      }
    } finally {
      setPrintingSlip(null);
      if (!asking) setPricingType('TN');
    }
  };
  
  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/wms/fg')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <ArrowRightLeft className="w-7 h-7" />
              <div>
                <h1 className="text-xl font-bold font-display">Design Transfer Slips</h1>
                <p className="text-sm text-[#E8DCC8]">Import & manage design transfer pick slips</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PrintMethodToggle screenKey="fg-design-transfer" dark />
            <Button
              variant="outline"
              size="sm"
              onClick={usbDevice ? () => setUsbDevice(null) : connectUSBPrinter}
              className={usbDevice ? "border-green-400 text-green-400 hover:bg-green-400/10" : "border-[#E8DCC8] text-[#E8DCC8] hover:bg-white/10"}
              data-testid="connect-usb-btn"
            >
              <Usb className="w-4 h-4 mr-1" />
              {usbDevice ? 'Connected' : 'Connect Printer'}
            </Button>
            <Button
              variant="outline"
              onClick={fetchSlips}
              className="border-[#E8DCC8] text-[#E8DCC8] hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {canManage && (
              <Button
                onClick={() => setImportDialogOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="import-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
            )}
          </div>
        </div>
      </header>
      
      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="IMPORTED">Imported</SelectItem>
                <SelectItem value="ASSIGNED">Assigned</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input
            placeholder="Search slip number or picker..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-xs"
            data-testid="search-input"
          />
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Pricing Type:</span>
            <Select value={pricingType} onValueChange={setPricingType}>
              <SelectTrigger className="w-32" data-testid="dt-pricing-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availablePricingTypes.map((p) => <SelectItem key={p} value={p} data-testid={`dt-pricing-type-opt-${p}`}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-gray-500">for sticker printing · back to TN after each print</span>
          </div>
          <div className="ml-auto text-sm text-gray-500">
            {filteredSlips.length} slip(s)
          </div>
        </div>
      </div>
      
      {/* Slips Table */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : filteredSlips.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No design transfer slips found</p>
                {canManage && (
                  <Button
                    onClick={() => setImportDialogOpen(true)}
                    variant="outline"
                    className="mt-4"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import Slips
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Slip No</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Total Qty</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Direct to Packing</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Imported</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSlips.map((slip) => {
                    const totalQty = slip.total_qty || 0;
                    const transferredQty = slip.items?.reduce((sum, item) => sum + (item.transferred_qty || 0), 0) || 0;
                    const progressPercent = totalQty > 0 ? Math.round((transferredQty / totalQty) * 100) : 0;
                    
                    return (
                      <TableRow key={slip.slip_number} data-testid={`slip-row-${slip.slip_number}`}>
                        <TableCell className="font-mono font-medium">
                          {slip.slip_number}
                          {slip.is_urgent && (
                            <Badge variant="destructive" className="ml-2 text-xs">Urgent</Badge>
                          )}
                        </TableCell>
                        <TableCell>{slip.total_items}</TableCell>
                        <TableCell>{totalQty}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[slip.status] || 'bg-gray-100'}>
                            {slip.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {['IMPORTED', 'ASSIGNED'].includes(slip.status) ? (
                            <div className="flex flex-col gap-1">
                              <Button
                                variant={slip.direct_to_packing ? "default" : "outline"}
                                size="sm"
                                className={`text-xs h-7 ${slip.direct_to_packing ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'border-gray-300 text-gray-600'}`}
                                onClick={() => handleToggleDirectToPacking(slip.slip_number)}
                                data-testid={`dtp-toggle-${slip.slip_number}`}
                              >
                                <Package className="w-3 h-3 mr-1" />
                                {slip.direct_to_packing ? 'ON' : 'OFF'}
                              </Button>
                              {slip.direct_to_packing && (
                                slip.tagged_fg_pick_slip ? (
                                  <Badge variant="outline" className="text-xs border-green-400 text-green-700">
                                    FG: {slip.tagged_fg_pick_slip}
                                  </Badge>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs h-6 border-blue-300 text-blue-600 hover:bg-blue-50"
                                    onClick={() => handleOpenTagDialog(slip.slip_number)}
                                    data-testid={`tag-fg-slip-${slip.slip_number}`}
                                  >
                                    Tag FG Slip
                                  </Button>
                                )
                              )}
                            </div>
                          ) : slip.direct_to_packing ? (
                            <Badge variant="outline" className="text-xs border-orange-400 text-orange-700">
                              {slip.tagged_fg_pick_slip ? `FG: ${slip.tagged_fg_pick_slip}` : 'DTP'}
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {slip.assigned_to_name || (
                            <span className="text-gray-400 italic">Unassigned</span>
                          )}
                          {slip.status === 'COMPLETED' && slip.pp_staff_name && (
                            <div
                              className="text-xs text-emerald-700 mt-1 flex items-center gap-1"
                              data-testid={`handover-staff-${slip.slip_number}`}
                              title="Regular processing staff to hand the relabeled pieces over to"
                            >
                              <ArrowRightLeft className="w-3 h-3" />
                              Handover: <span className="font-medium">{slip.pp_staff_name}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={progressPercent} className="w-20 h-2" />
                            <span className="text-xs text-gray-500">{progressPercent}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(slip.import_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetail(slip.slip_number)}
                              data-testid={`view-${slip.slip_number}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {canManage && slip.status === 'IMPORTED' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenAssign(slip.slip_number)}
                                data-testid={`assign-${slip.slip_number}`}
                              >
                                <Users className="w-4 h-4" />
                              </Button>
                            )}
                            {['IMPORTED', 'ASSIGNED', 'IN_PROGRESS'].includes(slip.status) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStartPicking(slip.slip_number)}
                                className="text-green-600 hover:text-green-700"
                                data-testid={`start-${slip.slip_number}`}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            )}
                            {slip.status === 'IN_PROGRESS' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCompleteSlip(slip.slip_number)}
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                data-testid={`complete-slip-${slip.slip_number}`}
                                title="Complete slip (skip remaining items) so stickers can be printed"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {['COMPLETED', 'PARTIAL'].includes(slip.status) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePrintStickers(slip.slip_number)}
                                disabled={printingSlip === slip.slip_number}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                data-testid={`print-stickers-${slip.slip_number}`}
                                title="Print target design stickers for transferred USNs"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Admin Delete Button */}
                            {user?.role === 'Admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSlip(slip.slip_number)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-${slip.slip_number}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
      
      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Import Design Transfer Slips
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Expected CSV Format:</h4>
              <code className="text-xs text-blue-700 block">
                SlipNo, Source Barcode, Target Barcode, Qty, Priority (optional)
              </code>
              <p className="text-xs text-blue-600 mt-2">
                Each slip number groups multiple transfer items. The system will automatically find source locations.
              </p>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
                disabled={importing}
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-600">Click to select CSV file</p>
                <p className="text-sm text-gray-400 mt-1">or drag and drop</p>
              </label>
            </div>
            
            {importing && importProgress && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="font-medium">
                    {importProgress.phase === 'uploading' && 'Uploading...'}
                    {importProgress.phase === 'starting' && `Processing ${importProgress.total} records...`}
                    {importProgress.phase === 'completed' && 'Import completed!'}
                  </span>
                </div>
                {importProgress.phase === 'completed' && (
                  <div className="text-sm text-gray-600">
                    <p>✓ Imported: {importProgress.imported}</p>
                    <p>⊘ Skipped (duplicates): {importProgress.skipped}</p>
                    {importProgress.errors?.length > 0 && (
                      <p className="text-red-600">✗ Errors: {importProgress.errors.length}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)} disabled={importing}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Slip: {selectedSlip?.slip_number}
              <Badge className={statusColors[selectedSlip?.status] || ''}>
                {selectedSlip?.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedSlip && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gray-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-500">Total Items</p>
                    <p className="text-2xl font-bold">{selectedSlip.total_items}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-500">Total Qty</p>
                    <p className="text-2xl font-bold">{selectedSlip.total_qty}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-500">Assigned To</p>
                    <p className="text-lg font-medium">{selectedSlip.assigned_to_name || '-'}</p>
                  </CardContent>
                </Card>
              </div>

              {selectedSlip.status === 'COMPLETED' && selectedSlip.pp_staff_name && (
                <div
                  className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm"
                  data-testid="detail-handover-staff"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  Handover the relabeled pieces to (regular processing):{' '}
                  <span className="font-semibold">{selectedSlip.pp_staff_name}</span>
                </div>
              )}
              
              {/* Items */}
              <div>
                <h4 className="font-medium mb-2">Transfer Items</h4>
                <div className="space-y-2">
                  {selectedSlip.items?.map((item, idx) => {
                    const isExpanded = expandedItems[idx];
                    const progressPercent = item.required_qty > 0 
                      ? Math.round((item.transferred_qty || 0) / item.required_qty * 100) 
                      : 0;
                    
                    return (
                      <Card key={idx} className="border">
                        <CardContent className="p-3">
                          <div 
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => toggleItemExpand(idx)}
                          >
                            <div className="flex items-center gap-4">
                              <Badge className={item.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                {item.status}
                              </Badge>
                              <div>
                                <span className="font-mono text-sm">{item.source_barcode}</span>
                                <span className="mx-2 text-gray-400">→</span>
                                <span className="font-mono text-sm">{item.target_barcode}</span>
                              </div>
                              <span className="text-sm text-gray-500">
                                Qty: {item.transferred_qty || 0}/{item.required_qty}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Progress value={progressPercent} className="w-20 h-2" />
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Source Item</p>
                                <p className="font-medium">{item.source_design_name}</p>
                                <p className="text-gray-600">{item.source_shade} | {item.source_size}</p>
                                <p className="text-gray-500 text-xs mt-1">{item.source_description}</p>
                                {item.source_locations?.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500">Locations:</p>
                                    {item.source_locations.slice(0, 3).map((loc, i) => (
                                      <Badge key={i} variant="outline" className="mr-1 mt-1 text-xs">
                                        {loc.bin_code}: {loc.available_qty}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Target Item</p>
                                <p className="font-medium">{item.target_design_name}</p>
                                <p className="text-gray-600">{item.target_shade} | {item.target_size}</p>
                                <p className="text-gray-500 text-xs mt-1">{item.target_description}</p>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
            {['IMPORTED', 'ASSIGNED', 'IN_PROGRESS'].includes(selectedSlip?.status) && (
              <Button onClick={() => {
                setDetailDialogOpen(false);
                handleStartPicking(selectedSlip.slip_number);
              }} className="bg-green-600 hover:bg-green-700">
                <Play className="w-4 h-4 mr-2" />
                Start Picking
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Assign Slip: {assignSlipNumber}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Select Picker</label>
              <Select value={selectedPicker} onValueChange={setSelectedPicker}>
                <SelectTrigger data-testid="picker-select">
                  <SelectValue placeholder="Choose a picker..." />
                </SelectTrigger>
                <SelectContent>
                  {pickers.length === 0 ? (
                    <SelectItem value="_none" disabled>No pickers available</SelectItem>
                  ) : (
                    pickers.map((picker) => (
                      <SelectItem key={picker.staff_id} value={picker.staff_id}>
                        {picker.staff_name || picker.name} ({picker.staff_id})
                        {picker.status && picker.status !== 'available' && (
                          <span className="ml-2 text-xs text-orange-500">({picker.status})</span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={!selectedPicker}>
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag FG Pick Slip Dialog */}
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Tag FG Pick Slip to DT Slip {tagSlipNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search by slip number, order, or design..."
                value={fgSlipSearch}
                onChange={(e) => {
                  setFgSlipSearch(e.target.value);
                  searchMatchingFgSlips(tagSlipNumber, e.target.value);
                }}
                data-testid="fg-slip-search"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {searchingFgSlips ? (
                <div className="text-center py-4 text-gray-500">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-1" />Searching...
                </div>
              ) : matchingFgSlips.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm" data-testid="no-fg-slips-msg">
                  No matching FG Pick Slips found.
                  <div className="mt-1 text-xs">
                    Eligible slips must be IMPORTED / PENDING / ASSIGNED /
                    IN_PROGRESS / PARTIAL, not already tagged to another DT
                    slip, and contain at least one of this DT slip's target
                    barcodes.
                  </div>
                </div>
              ) : (
                matchingFgSlips.map((fs) => (
                  <div key={fs.pick_slip_number} className="border rounded p-3 hover:bg-gray-50 cursor-pointer" onClick={() => handleTagFgSlip(fs.pick_slip_number)} data-testid={`fg-slip-option-${fs.pick_slip_number}`}>
                    <div className="flex justify-between items-center">
                      <span className="font-mono font-medium">{fs.pick_slip_number}</span>
                      <Badge variant="outline" className="text-xs">{fs.status}</Badge>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {fs.matching_items.map((m, i) => (
                        <span key={i} className="mr-3">
                          {m.design_name || m.barcode}: {m.pending_qty} pending
                          {m.order_number ? ` (${m.order_number})` : ''}
                        </span>
                      ))}
                    </div>
                    <div className="text-xs text-orange-600 mt-1 font-medium">
                      Total pending: {fs.total_matching_qty} pcs
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagDialogOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PricingTypePrompt prompt={ratePrompt} onCancel={() => { setRatePrompt(null); setPricingType('TN'); }}
        onPick={(p) => { const code = ratePrompt.code; setRatePrompt(null); setPricingType(p); toast.info(`Printing ${code} with ${p} rates`); handlePrintStickers(code, p); }} />
    </div>
  );
};

export default FGDesignTransferSlips;
